import { useState, useEffect, useContext } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import './Orders.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

const STATUS_LABELS = {
    pending: 'En attente',
    confirmed: 'Confirmée',
    shipped: 'Expédiée',
    delivered: 'Livrée',
    cancelled: 'Annulée',
    refunded: 'Remboursée',
}

const FILTERS = [
    { value: '', label: 'Toutes' },
    { value: 'pending', label: 'En attente' },
    { value: 'confirmed', label: 'Confirmées' },
    { value: 'shipped', label: 'Expédiées' },
    { value: 'delivered', label: 'Livrées' },
    { value: 'cancelled', label: 'Annulées' },
    { value: 'refunded', label: 'Remboursées' },
]

const Orders = () => {
    const { user } = useContext(AuthContext)
    const brandId = user?.brand

    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('')
    const [selectedId, setSelectedId] = useState(null)
    const [showShipForm, setShowShipForm] = useState(false)
    const [shipData, setShipData] = useState({ trackingNumber: '', carrier: '' })
    const [saving, setSaving] = useState(false)
    const [refunding, setRefunding] = useState(false)

    const loadOrders = async () => {
        if (!brandId) return
        setLoading(true)
        try {
            const qs = statusFilter ? `?status=${statusFilter}` : ''
            const data = await apiFetch(`/brand/${brandId}/orders${qs}`)
            setOrders(data.orders || [])
        } catch { setOrders([]) }
        finally { setLoading(false) }
    }

    useEffect(() => { loadOrders() }, [brandId, statusFilter])

    const selected = orders.find(o => o._id === selectedId)

    const handleSelect = (id) => {
        setSelectedId(prev => prev === id ? null : id)
        setShowShipForm(false)
        setShipData({ trackingNumber: '', carrier: '' })
    }

    const handleShip = async () => {
        if (!selected) return
        setSaving(true)
        try {
            await apiFetch(`/brand/${brandId}/orders/${selected._id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({
                    status: 'shipped',
                    trackingNumber: shipData.trackingNumber || undefined,
                    carrier: shipData.carrier || undefined,
                }),
            })
            setOrders(prev => prev.map(o =>
                o._id === selected._id
                    ? { ...o, status: 'shipped', trackingNumber: shipData.trackingNumber, carrier: shipData.carrier }
                    : o
            ))
            setShowShipForm(false)
        } catch (err) {
            alert(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleRefund = async () => {
        if (!selected) return
        if (!window.confirm('Confirmer le remboursement de cette commande ?')) return
        setRefunding(true)
        try {
            await apiFetch(`/brand/${brandId}/orders/${selected._id}/refund`, { method: 'POST' })
            setOrders(prev => prev.map(o =>
                o._id === selected._id ? { ...o, status: 'refunded' } : o
            ))
        } catch (err) {
            alert(err.message)
        } finally {
            setRefunding(false)
        }
    }

    const itemsSummary = (order) =>
        order.items.map(i => `${i.name} ×${i.qty}`).join(', ')

    if (loading) return <div className="orders-loading">Chargement...</div>

    return (
        <div className="orders-page">
            <div className="orders-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <h2>Commandes</h2>
                    <span className="orders-count">{orders.length} commande{orders.length !== 1 ? 's' : ''}</span>
                </div>
            </div>

            <div className="orders-filters">
                {FILTERS.map(f => (
                    <button
                        key={f.value}
                        className={`orders-filter-btn${statusFilter === f.value ? ' orders-filter-btn--active' : ''}`}
                        onClick={() => { setStatusFilter(f.value); setSelectedId(null) }}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {orders.length === 0 ? (
                <p className="orders-empty">Aucune commande{statusFilter ? ` avec le statut "${STATUS_LABELS[statusFilter]}"` : ''}.</p>
            ) : (
                <div className="orders-list">
                    {orders.map(order => (
                        <div key={order._id}>
                            <div
                                className={`orders-row${selectedId === order._id ? ' orders-row--selected' : ''}`}
                                onClick={() => handleSelect(order._id)}
                            >
                                <div className="orders-row-left">
                                    <span className="orders-row-id">#{order._id.slice(-8)}</span>
                                    <span className="orders-row-items">{itemsSummary(order)}</span>
                                    <span className="orders-row-date">
                                        {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                                <div className="orders-row-right">
                                    <span className="orders-row-amount">
                                        {Number(order.totalAmount).toFixed(2)} €
                                    </span>
                                    <span className={`orders-status orders-status--${order.status}`}>
                                        {STATUS_LABELS[order.status] || order.status}
                                    </span>
                                </div>
                            </div>

                            {selectedId === order._id && (
                                <div className="orders-detail">
                                    <div className="orders-detail-header">
                                        <span className="orders-detail-id">Commande #{order._id}</span>
                                        <span className={`orders-status orders-status--${order.status}`}>
                                            {STATUS_LABELS[order.status] || order.status}
                                        </span>
                                    </div>

                                    <div className="orders-detail-section">
                                        <h4>Articles</h4>
                                        <div className="orders-detail-items">
                                            {order.items.map((item, i) => (
                                                <div key={i} className="orders-detail-item">
                                                    <span className="orders-detail-item-name">{item.name}</span>
                                                    <span className="orders-detail-item-meta">
                                                        ×{item.qty} · {Object.values(item.attributes || {}).join(', ')}
                                                    </span>
                                                    <span className="orders-detail-item-price">
                                                        {(item.unitPrice * item.qty).toFixed(2)} €
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="orders-detail-total">
                                            Total : {Number(order.totalAmount).toFixed(2)} €
                                        </div>
                                    </div>

                                    {order.shippingAddress && (
                                        <div className="orders-detail-section">
                                            <h4>Livraison</h4>
                                            <div className="orders-detail-address">
                                                {order.shippingAddress.line1 && <div>{order.shippingAddress.line1}</div>}
                                                {order.shippingAddress.line2 && <div>{order.shippingAddress.line2}</div>}
                                                <div>{order.shippingAddress.zip || order.shippingAddress.postalCode} {order.shippingAddress.city}</div>
                                                <div>{order.shippingAddress.country}</div>
                                            </div>
                                        </div>
                                    )}

                                    {order.trackingNumber && (
                                        <div className="orders-tracking">
                                            Suivi : <strong>{order.trackingNumber}</strong>
                                            {order.carrier && ` · ${order.carrier}`}
                                        </div>
                                    )}

                                    <div className="orders-detail-actions">
                                        {order.status === 'confirmed' && !showShipForm && (
                                            <button className="orders-btn-ship" onClick={() => setShowShipForm(true)}>
                                                Marquer comme expédiée
                                            </button>
                                        )}
                                        {['confirmed', 'shipped', 'delivered'].includes(order.status) && (
                                            <button
                                                className="orders-btn-refund"
                                                onClick={handleRefund}
                                                disabled={refunding}
                                            >
                                                {refunding ? 'Remboursement...' : 'Rembourser'}
                                            </button>
                                        )}
                                    </div>

                                    {showShipForm && order.status === 'confirmed' && (
                                        <div className="orders-ship-form">
                                            <div className="orders-ship-form-row">
                                                <input
                                                    type="text"
                                                    placeholder="Numéro de suivi (optionnel)"
                                                    value={shipData.trackingNumber}
                                                    onChange={e => setShipData(p => ({ ...p, trackingNumber: e.target.value }))}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Transporteur (Colissimo, DHL…)"
                                                    value={shipData.carrier}
                                                    onChange={e => setShipData(p => ({ ...p, carrier: e.target.value }))}
                                                />
                                            </div>
                                            <div className="orders-ship-form-actions">
                                                <button className="orders-btn-cancel-sm" onClick={() => setShowShipForm(false)}>Annuler</button>
                                                <button className="orders-btn-confirm" onClick={handleShip} disabled={saving}>
                                                    {saving ? '...' : 'Confirmer l\'expédition'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default Orders
